import {
  getPlayersSearchOptions,
  getTeamsSearchOptions,
  type PlayerPublic,
  type SideType,
} from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  Label,
  Spinner,
} from "@win-reality/react-ui";
import { useEffect, useState } from "react";

import { VisibilityGroupSelect } from "@/components/VisibilityGroupSelect";
import { GamePlayer } from "@/types/game";

import { mapTeamDBToOption, type TeamOption } from "../teamOption";

export { mapTeamDBToOption };

export type TeamSelectData = {
  teamRoster: GamePlayer[];
  teamName: string;
  teamUuid: string;
};

type TeamSelectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TeamSelectData) => void;
  title?: string;
  confirmLabel?: string;
  isConfirmPending?: boolean;
};

const sideTypeToHandednessMap: Record<SideType, "R" | "L" | "S"> = {
  right: "R",
  left: "L",
  switch: "S",
};

export const mapPublicPlayerToGamePlayer = (
  player: PlayerPublic,
  /** The player's roster jersey number, if known */
  jerseyNumber = "",
): GamePlayer => {
  const [lastName, firstName] = player.known_names?.[0]?.split(",") ?? [];

  return {
    uuid: player.external_ids?.[0] ?? "",
    firstName: firstName?.trim() ?? "",
    lastName: lastName?.trim() ?? "",
    birthdate: "",
    jerseyNumber,
    // Unknown sides fall back to R: the lineup check rejects anything else
    batHand: sideTypeToHandednessMap[player.hit_side as SideType] ?? "R",
    throwHand: sideTypeToHandednessMap[player.pitch_side as SideType] ?? "R",
  };
};

/**
 * TeamSelectModal - Dialog for searching and selecting a team
 *
 * Flow:
 * 1. User selects visibility group
 * 2. User searches for team by name
 * 3. Team roster is loaded automatically
 * 4. User confirms import
 */
export const TeamSelectModal = ({
  isOpen,
  onClose,
  onSubmit,
  title = "Select Team",
  confirmLabel = "Import Team",
  isConfirmPending = false,
}: TeamSelectModalProps) => {
  const [visibilityGroup, setVisibilityGroup] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTeamUuid, setSelectedTeamUuid] = useState<string | null>(null);
  const [selectedTeamName, setSelectedTeamName] = useState<string | null>(null);
  const [teamRoster, setTeamRoster] = useState<GamePlayer[]>([]);

  // Query: Search teams
  const { data: teamsData, isLoading: isLoadingTeams } = useQuery({
    ...getTeamsSearchOptions({
      query: {
        name: debouncedSearch,
        visibilityGroup: visibilityGroup!,
      },
    }),
    enabled: !!visibilityGroup,
    select: (result) => result?.teams?.map(mapTeamDBToOption) ?? [],
  });

  // Query: Load team roster
  const { data: rosterData, isLoading: isLoadingRoster } = useQuery({
    ...getPlayersSearchOptions({
      query: {
        visibilityGroup: visibilityGroup!,
        teamId: selectedTeamUuid!,
      },
    }),
    enabled: !!selectedTeamUuid && !!visibilityGroup,
    select: (result) =>
      result?.players?.map((player) => mapPublicPlayerToGamePlayer(player)) ??
      [],
  });

  useEffect(() => {
    if (rosterData) {
      setTeamRoster(rosterData);
    }
  }, [rosterData]);

  useEffect(() => {
    if (!isOpen) {
      cleanUpState();
      setVisibilityGroup(null);
    }
  }, [isOpen]);

  const cleanUpState = () => {
    setSearchValue("");
    setDebouncedSearch("");
    setSelectedTeamUuid(null);
    setSelectedTeamName(null);
    setTeamRoster([]);
  };

  const handleTeamSelect = (teamUuid: string) => {
    const team = teamsData?.find((t) => t.value === teamUuid);
    if (team) {
      setSelectedTeamUuid(team.value);
      setSelectedTeamName(team.label);
    }
  };

  const handleConfirmImport = () => {
    if (!selectedTeamName || !selectedTeamUuid || teamRoster.length === 0) {
      return;
    }

    onSubmit({
      teamRoster,
      teamName: selectedTeamName,
      teamUuid: selectedTeamUuid,
    });
  };

  const handleClose = () => {
    cleanUpState();
    setVisibilityGroup(null);
    onClose();
  };

  const canSubmit =
    !!selectedTeamUuid && teamRoster.length > 0 && !isLoadingRoster;

  const teamOptions: TeamOption[] = teamsData ?? [];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      defaultCancelButton={false}
      size="md"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-4 py-6">
              {/* Visibility Group Selector */}
              <div>
                <Label className="mb-2 block text-sm font-medium">
                  Visibility Group
                </Label>
                <VisibilityGroupSelect
                  value={visibilityGroup}
                  onChange={(value) => {
                    setVisibilityGroup(value);
                    cleanUpState();
                  }}
                />
              </div>

              {/* Team Search */}
              <div>
                <Label className="mb-2 block text-sm font-medium">
                  Team Name
                </Label>
                {visibilityGroup ? (
                  <Autocomplete
                    key={visibilityGroup}
                    selectedValue={selectedTeamUuid ?? undefined}
                    onSelectedValueChange={(value) =>
                      handleTeamSelect(value as string)
                    }
                    searchValue={searchValue}
                    onSearchValueChange={setSearchValue}
                    onSearchDebouncedValueChange={setDebouncedSearch}
                    items={teamOptions}
                    isLoading={isLoadingTeams}
                    placeholder="Search teams..."
                    emptyMessage="No teams found"
                    variant="dark"
                    className="w-full"
                    popoverContentClassName="z-[110]"
                    itemRenderer={(option) => (
                      <span className="text-accent-dark-1">{option.label}</span>
                    )}
                  />
                ) : (
                  <div className="flex h-10 items-center rounded-sm border border-border-input bg-input px-3">
                    <span className="text-sm font-light text-placeholder">
                      Select visibility group first
                    </span>
                  </div>
                )}
              </div>

              {/* Loading roster indicator */}
              {isLoadingRoster && selectedTeamUuid && (
                <div className="flex items-center justify-center gap-2 py-4">
                  <Spinner />
                  <span className="text-sm text-accent-dark-3">
                    Loading roster...
                  </span>
                </div>
              )}

              {/* Roster loaded indicator */}
              {!isLoadingRoster && teamRoster.length > 0 && (
                <div className="rounded-md bg-green/10 p-3 text-center text-sm text-green">
                  {teamRoster.length} players found on roster
                </div>
              )}

              {/* No roster warning */}
              {!isLoadingRoster &&
                selectedTeamUuid &&
                teamRoster.length === 0 && (
                  <div className="rounded-md bg-error/10 p-3 text-center text-sm text-error">
                    No players found on this team&apos;s roster
                  </div>
                )}
            </div>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                type="button"
                className="underline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                onClick={handleConfirmImport}
                isDisabled={!canSubmit}
                isLoading={isConfirmPending}
              >
                {confirmLabel}
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};
